// ============================================================================
// useMinimizableForm — unified minimize/restore for ALL form modal modes
// ============================================================================
// Covers create / edit / copy modes in a single hook call. View modals don't
// have form state — they use useModalMinimize.minimize() directly.
//
// The hook owns: form-data ref tracking, restore-flag bookkeeping, dirty
// detection (against the populated-at-open baseline, NOT just emptyForm),
// modal-id derivation per mode, title generation. The caller provides a
// per-mode "populate on open" function and a title builder; the hook
// handles everything else.
//
// MODE SEMANTICS (matches ENTERPRISE_SCREEN_CHECKLIST Layer 4.6):
//
//   create — counter-based, multi-instance. createInstanceId drives the
//            "(N)" suffix.
//   edit   — record-keyed. recordKey is the source record's stable code.
//   copy   — record-keyed. Same as edit.
//
// MODAL IDS (derived by the hook):
//   create: `${tabId}-form-modal-create-${createInstanceId}`
//   edit:   `${tabId}-form-modal-edit-${recordKey}`
//   copy:   `${tabId}-form-modal-copy-${recordKey}`
//
// USAGE — single-mode (Create-only):
//
//   const { handleMinimize, isDirty } = useMinimizableForm<FormState>({
//     open: isOpen,
//     mode: 'create',
//     form, setForm, emptyForm: EMPTY_FORM,
//     tabId, moduleName,
//     createInstanceId,
//     // populateOnOpen omitted → defaults to returning emptyForm
//     buildTitle: ({ form: f, createInstanceId: id }) =>
//       f.name ? `Add - ${f.name} (${id})` : `Add API Key (${id})`,
//     onMinimized: ({ createInstanceId: id }) => parent?.(id),
//     onClose,
//   });
//
// USAGE — multi-mode (Create + Edit + Copy in one modal):
//
//   const populateOnOpen = useCallback(() => {
//     if (mode === 'create') return { ...EMPTY_FORM, effectiveFrom: today };
//     if (mode === 'edit'   && record) return { code: record.code, ...etc };
//     if (mode === 'copy'   && record) return { code: '', name: `Copy of ${record.name}`, ... };
//     return EMPTY_FORM;
//   }, [mode, record]);
//
//   const { handleMinimize, isDirty } = useMinimizableForm<FormState>({
//     open: isOpen, mode,
//     form, setForm, emptyForm: EMPTY_FORM,
//     tabId, moduleName,
//     createInstanceId,
//     recordKey: record?.code,
//     populateOnOpen,
//     buildTitle: ({ mode, form: f, createInstanceId, recordKey }) =>
//       mode === 'create' ? `Add - ${f.name} (${createInstanceId})` :
//       mode === 'edit'   ? `Edit - ${recordKey}` :
//                           `Copy - ${recordKey}`,
//     onMinimized: ({ mode, createInstanceId, recordKey }) =>
//       parent.handleRestoreModal(mode, createInstanceId, recordKey),
//     onClose,
//   });
//
// IMPORTANT: Callers must NOT add their own `useEffect([open])` that
// populates form. The hook owns the open-transition effect. Pass your
// populate logic via `populateOnOpen`.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useModalMinimize } from '../../contexts/ModalMinimizeContext';

export type MinimizableFormMode = 'create' | 'edit' | 'copy';

export interface BuildTitleArgs<T> {
  mode: MinimizableFormMode;
  form: T;
  createInstanceId?: string;
  recordKey?: string;
}

export interface OnMinimizedArgs {
  mode: MinimizableFormMode;
  createInstanceId?: string;
  recordKey?: string;
}

interface UseMinimizableFormOptions<T> {
  /** True when the modal is rendering its visible content. */
  open: boolean;
  /** Which mode the modal is in. Affects ID derivation. */
  mode: MinimizableFormMode;
  /** Current form state. */
  form: T;
  /** State setter — must be the React useState setter (stable reference). */
  setForm: React.Dispatch<React.SetStateAction<T>>;
  /**
   * Empty/initial form value used as the DEFAULT populateOnOpen result.
   * Must be a STABLE reference (declare as module-level `const`).
   */
  emptyForm: T;
  /** Tab id this modal lives in. When undefined, minimize is a no-op. */
  tabId: string | undefined;
  /** Module display name used in the minimize bar entry. */
  moduleName: string;
  /**
   * Create-only: the current "Add" instance ordinal. Drives the (N)
   * suffix and the modal id. Ignored for edit/copy.
   */
  createInstanceId?: string;
  /**
   * Edit/copy: the source record's stable identifier (typically
   * `record.code`). Drives the modal id so clicking Edit/Copy on the
   * same record finds + restores the existing minimized instance.
   * Ignored for create.
   */
  recordKey?: string;
  /**
   * Called on every FRESH open (not restore). Returns the form value
   * to populate. Defaults to `emptyForm`. For multi-mode modals, this
   * is where the per-mode initialization (populate from record, etc)
   * lives. Wrap in `useCallback` for stability.
   */
  populateOnOpen?: () => T;
  /**
   * Builds the minimize-bar entry's title from the captured snapshot.
   * Wrap in `useCallback` for stable identity.
   */
  buildTitle: (args: BuildTitleArgs<T>) => string;
  /**
   * Fired when the user clicks Resume on a minimized entry. Form data
   * is restored INSIDE the hook (closure-captured snapshot). Parent
   * uses the callback to restore mode + record selection.
   */
  onMinimized?: (args: OnMinimizedArgs) => void;
  /** Called to close the modal after minimize completes. */
  onClose: () => void;
}

interface UseMinimizableFormReturn<T> {
  /** Bind to your minimize button's onClick. */
  handleMinimize: () => void;
  /**
   * True when the current form differs from the populated-at-open
   * baseline. Use for "discard changes?" guards and to pass to
   * `useModalMinimize.minimize({ isDirty })`.
   */
  isDirty: boolean;
  /**
   * Reset the dirty-comparison baseline to a new form value. Used in
   * niche cases where the form is repopulated AFTER initial open —
   * e.g., a "refresh from server" flow after a concurrency conflict.
   * Without this call, the user would still appear "dirty" against the
   * original-at-open snapshot even though they accepted the server's
   * latest values.
   */
  resetBaseline: (newForm: T) => void;
}

/**
 * Build the framework-canonical modal id. Exported so screens can call
 * `isMinimized(buildModalId(...))` in their row-action handlers without
 * duplicating the format string.
 */
export function buildModalId(
  tabId: string,
  mode: MinimizableFormMode,
  args: { createInstanceId?: string; recordKey?: string },
): string {
  if (mode === 'create') {
    return `${tabId}-form-modal-create-${args.createInstanceId ?? 'default'}`;
  }
  return `${tabId}-form-modal-${mode}-${args.recordKey ?? 'new'}`;
}

/**
 * Unified minimize/restore hook for form modals (Create / Edit / Copy).
 * View modals — no form state — use `useModalMinimize().minimize()` directly.
 */
export function useMinimizableForm<T extends object>(
  opts: UseMinimizableFormOptions<T>,
): UseMinimizableFormReturn<T> {
  const {
    open,
    mode,
    form,
    setForm,
    emptyForm,
    tabId,
    moduleName,
    createInstanceId,
    recordKey,
    populateOnOpen,
    buildTitle,
    onMinimized,
    onClose,
  } = opts;

  const { minimize } = useModalMinimize();

  // Always-current refs — handleMinimize reads these at minimize-time so
  // its useCallback can have stable deps without stale-closure traps.
  const formDataRef = useRef(form);
  formDataRef.current = form;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const recordKeyRef = useRef(recordKey);
  recordKeyRef.current = recordKey;
  const populateOnOpenRef = useRef(populateOnOpen);
  populateOnOpenRef.current = populateOnOpen;

  // JSON snapshot of the form AT FRESH OPEN — the baseline for isDirty.
  // Set inside the open-transition effect; restored in onRestore.
  const openSnapshotRef = useRef<string>('');

  // TRUE during restore — set inside onRestore BEFORE the modal re-opens
  // so the upcoming useEffect[open] skips the populate logic.
  const isRestoreRef = useRef(false);

  // Open-transition effect: populate form on fresh open; skip on restore.
  useEffect(() => {
    if (!open) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      return;
    }
    const initial = populateOnOpenRef.current
      ? populateOnOpenRef.current()
      : emptyForm;
    setForm(initial);
    openSnapshotRef.current = JSON.stringify(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Dirty detection: current form vs the populated-at-open baseline.
  const isDirty = useMemo(() => {
    if (!openSnapshotRef.current) return false;
    return JSON.stringify(form) !== openSnapshotRef.current;
  }, [form]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;

    const savedMode = modeRef.current;
    const savedForm = { ...formDataRef.current } as T;
    const savedSnapshot = openSnapshotRef.current;
    const savedCreateInstanceId = createInstanceIdRef.current;
    const savedRecordKey = recordKeyRef.current;
    const savedIsDirty = savedSnapshot
      ? JSON.stringify(savedForm) !== savedSnapshot
      : false;

    const id = buildModalId(tabId, savedMode, {
      createInstanceId: savedCreateInstanceId,
      recordKey:        savedRecordKey,
    });

    const title = buildTitle({
      mode:             savedMode,
      form:             savedForm,
      createInstanceId: savedCreateInstanceId,
      recordKey:        savedRecordKey,
    });

    minimize({
      id,
      tabId,
      moduleName,
      title,
      isDirty: savedIsDirty,
      onRestore: () => {
        // ORDER MATTERS — set the flag BEFORE setForm + onMinimized so
        // the upcoming open→true transition's useEffect skips populate.
        isRestoreRef.current = true;
        setForm(savedForm);
        // Restore the dirty baseline so the comparison stays correct
        // (the modal stayed mounted, but defending against re-mount).
        openSnapshotRef.current = savedSnapshot;
        onMinimized?.({
          mode:             savedMode,
          createInstanceId: savedCreateInstanceId,
          recordKey:        savedRecordKey,
        });
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, buildTitle, setForm, onClose, onMinimized]);

  const resetBaseline = useCallback((newForm: T) => {
    openSnapshotRef.current = JSON.stringify(newForm);
  }, []);

  return { handleMinimize, isDirty, resetBaseline };
}

export default useMinimizableForm;
